import { f7 } from 'framework7-react';
import perse from 'html-react-parser';
import { FC } from 'react';
import { getWarrantiesMissDocument } from '../../WarrantyManagement/WarrantyManagementHelper';
import { BannerList } from '../BannerList/BannerList';
import { CarInfoCard } from '../CarInfoCard/CarInfoCard';
import { CardRecommend } from '../CardRecommend/CardRecommend';
import { RequestMaintenanceButton } from '../RequestMaintenanceButton/RequestMaintenanceButton';
import { ServicePromotionCard } from '../ServicePromotionCard/ServicePromotionCard';
import style from './homeStyle.module.css';
import { AdTelemileage } from '@/components/projects/AdTelemileage/AdTelemileage';
import { BrainTraining } from '@/components/projects/BrainTraining/BrainTraining';
import { ButtonIconVertical } from '@/components/projects/ButtonIconVertical/ButtonIconVertical';
import { ButtonIconVerticalSkeleton } from '@/components/projects/ButtonIconVertical/ButtonIconVerticalSkeleton';
import { RankBanner } from '@/components/projects/RankBanner/RankBanner';
import { IconCard } from '@/components/uiParts/Card/IconCard/IconCard';
import { Skeleton } from '@/components/uiParts/Skeleton/Skeleton';
import { PageTemplateV2 } from '@/components/uiParts/Template/PageTemplate/PageTemplateV2';
import { isPc } from '@/config/device';
import { SelectedTenant } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { affiliateId } from '@/consts/shop';
import { Affiliate } from '@/types/api/authApi';
import { Banner } from '@/types/api/bannerApi';
import { Car, Notification } from '@/types/api/customerApi';
import { LinkDriveOwner } from '@/types/api/infoApi';
import { Category } from '@/types/api/serviceApi';
import { SubscriptionInheritanceStatus } from '@/types/api/subscription';
import { isDelivered } from '@/utils/car';
import {
  isAdContractorData,
  isLinkDriveOwnerData,
  isShowDriveRecordButton,
} from '@/utils/home';
import { storeDispatch } from '@/utils/store';

interface HomeProps {
  isBasicInfoLoaded: boolean;
  isBrainTrainingDisplay: boolean;
  isMileageDisplay: boolean;
  selectedCar?: Car;
  isOpenCarList: boolean;
  linkDriveOwner: LinkDriveOwner | undefined;
  news: Notification | undefined;
  categories: Category[] | undefined;
  banners: Banner[] | undefined;
  isCategoriesLoading?: boolean;
  isBannersLoading?: boolean;
  affiliatesInfo: Affiliate[];
  isShowCarWashService: boolean;
  isShowCarAssetValue: boolean;
  isShowSwitchCar: boolean;
  isShowPrepaidInfo: boolean;
  isRegisterPaymentWarranty: boolean;
  isWarrantyExpired: boolean;
  isWarrantiesMissDocument: boolean;
  warrantyStatusText?: string;
  notificationCount?: number;
  hasSubscription?: boolean;
  hasSubscriptionNotice?: boolean;
  subscriptionNoticeId?: number;
  subscriptionLoaded?: boolean;
  subscriptionStatus?: number;
  subscriptionApplyStatus?: number;
  subscriptionInheritanceStatus?: SubscriptionInheritanceStatus | null;
  subscriptionTStockCarId?: number;
  subscriptionId?: number;
  isShowSubscription?: boolean;
  handleClickRegistCar: () => void;
  changeIsOpenCarList: (bool: boolean) => void;
  handleClickListImage: (
    car: Affiliate['cars'][0] | null,
    affiliateID: number,
  ) => Promise<void>;
  handleClickMaintenanceButton: () => void;
  handleClickNavigationNews: () => void;
  handleClickNews: (id: string) => void;
  handlePaymentRegistAlert: () => void;
  handlePaymentRegistAlertPrepaid: () => void;
  onRefetch?: (cb: () => void) => void;
  selectedTenant?: SelectedTenant;
}
export const Home: FC<HomeProps> = ({
  isBasicInfoLoaded,
  isBrainTrainingDisplay,
  isMileageDisplay,
  selectedCar,
  isOpenCarList,
  linkDriveOwner,
  news,
  categories,
  banners,
  isCategoriesLoading = false,
  isBannersLoading = false,
  affiliatesInfo,
  isShowCarWashService,
  isShowCarAssetValue,
  isShowSwitchCar,
  isShowPrepaidInfo,
  isRegisterPaymentWarranty,
  isWarrantyExpired,
  isWarrantiesMissDocument,
  warrantyStatusText,
  notificationCount,
  hasSubscription,
  hasSubscriptionNotice,
  subscriptionNoticeId,
  subscriptionLoaded,
  subscriptionStatus,
  subscriptionApplyStatus,
  subscriptionInheritanceStatus,
  subscriptionTStockCarId,
  subscriptionId,
  isShowSubscription = true,
  handleClickRegistCar,
  changeIsOpenCarList,
  handleClickListImage,
  handleClickMaintenanceButton,
  handleClickNavigationNews,
  handleClickNews,
  handlePaymentRegistAlert,
  handlePaymentRegistAlertPrepaid,
  onRefetch,
  selectedTenant,
}) => {
  const isrRoyalAutoId =
    store.state.customerInfo.m_affiliate_id === affiliateId.ROYAL;
  const isShowSelfScan =
    !!linkDriveOwner && !!linkDriveOwner?.id && !!linkDriveOwner.owner_id;
  const isShowDriveRecord = isShowDriveRecordButton({
    deviceDelDateStr: linkDriveOwner?.device_del_date,
    serviceEndDateStr: linkDriveOwner?.service_end_date,
    delFlg: linkDriveOwner?.del_flg,
    driveLogSendFlg: linkDriveOwner?.drivelog_send_flg,
  });
  const isAdContractor = isAdContractorData({
    linkDriveStatus: selectedCar?.linkdrive_status,
  });
  const hasInheritanceSubscription = !!subscriptionInheritanceStatus?.eligible;
  const hasSubscriptionInProgress = !!hasSubscriptionNotice && !hasSubscription;
  const isSameConditionInheritance =
    subscriptionInheritanceStatus?.condition_type === 'same_as_warranty';
  const handleClickSubscriptionInProgress = () => {
    // apply_status が 1（申込済・初回決済前）でも 2（初回決済済・継続登録前）でも
    // 残りは決済手続きなので、いずれも決済ページから再開させる
    f7.view.main.router.navigate(paths.subscriptionPayment, {
      props: {
        subscriptionId,
        tStockCarId: subscriptionTStockCarId,
      },
    });
  };

  const isLinkDriveOwner = isLinkDriveOwnerData({
    id: linkDriveOwner?.id,
    delFlg: linkDriveOwner?.del_flg,
  });

  const listExternalServiceURL = [
    {
      text: 'お車購入\nご相談',
      href: 'https://carstation.royal-auto.jp/',
      icon: 'search-black',
    },
    {
      text: '保険ご相談',
      href: 'https://royal-auto-service2.localinfo.jp/',
      icon: 'guarantee-insurance',
    },
    {
      text: 'アクセス',
      href: 'https://www.royal-auto.jp/shop',
      icon: 'home-map',
    },
  ];
  const handleRedirect = (href: string) => {
    window.open(href, '_blank');
  };
  const handleRedirectToMaintenanceReservation = () => {
    window.open(`https://onelink.to/n6x8cn`, '_blank');
  };

  // 既存車フロー判定: API の t_stock_car_id または localStorage から判定
  // localStorage 由来の場合は cid/sid を現在のユーザー・契約IDと照合して
  // 別ユーザーや旧契約の残骸による誤判定を防止
  const isExistingCarFlow = (() => {
    if (subscriptionTStockCarId) return true;
    const currentCid = (store.state.authInfo as any)?.m_customer_id;
    if (!currentCid) return false;
    try {
      const raw = window.localStorage.getItem('subscription_t_stock_car_id');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.tStockCarId !== 'number') return false;
      if (parsed.cid && parsed.cid !== currentCid) return false;
      if (parsed.sid && subscriptionId && parsed.sid !== subscriptionId)
        return false;
      return true;
    } catch {
      return false;
    }
  })();

  return (
    <PageTemplateV2
      pageName="Home"
      title="ホーム"
      hideNavBar
      contentStyle={{
        paddingBottom: 'calc(80px + env(safe-area-inset-bottom))',
      }}
      onRefresh={(done) => {
        if (onRefetch) {
          onRefetch(() => done());
        }
      }}
      onPageAfterIn={() => {
        if (onRefetch) {
          onRefetch(() => {});
        }
      }}
    >
      <div className={style['content']}>
        <CarInfoCard
          selectedTenant={selectedTenant}
          changeIsOpenCarList={changeIsOpenCarList}
          handleClickListImage={handleClickListImage}
          handleClickRegistCar={handleClickRegistCar}
          affiliatesInfo={affiliatesInfo}
          selectedCar={selectedCar}
          isOpenCarList={isOpenCarList}
          isBasicInfoLoaded={isBasicInfoLoaded}
          isShowSwitchCar={isShowSwitchCar}
          isShowCarAssetValue={isShowCarAssetValue}
          warrantyStatusText={warrantyStatusText}
          notificationCount={notificationCount}
        />
        <div className={style['notification-wrapper']}>
          {isWarrantiesMissDocument && (
            <div className="u-margin-bottom-xl">
              <IconCard
                isTransition
                iconType="confirm-notification"
                headingFontSize="14px"
                iconFontSize="32px"
                color="warning"
                heading="取引保証プラン有効化に必要な情報があります"
                onClick={() => {
                  storeDispatch(
                    'setCurrentWarranty',
                    'currentWarranty',
                    getWarrantiesMissDocument(store.state.warranties)[0] ||
                      store.state.warranties[1],
                  );
                  f7.views.main.router.navigate(paths.tradeGuaranteePlan);
                }}
              />
            </div>
          )}
          <div className={style['notification-title']}>
            <div className={style['notification-title-badge']}>
              <span className="u-font-bold u-font-size-xl">お知らせ</span>
            </div>
            <div className={style['view-all']}>
              <span onClick={handleClickNavigationNews}>全て見る</span>
              <i className="icon-right" />
            </div>
          </div>
          {hasInheritanceSubscription && (
            <div className="u-margin-top-sm">
              <IconCard
                isTransition
                iconType="confirm-notification"
                headingFontSize="14px"
                iconFontSize="30px"
                color="warning"
                heading={
                  isSameConditionInheritance
                    ? '今ならお得にサブスクへ移行できます'
                    : 'サブスクにお申し込みいただけます'
                }
                supplement={perse(
                  isSameConditionInheritance
                    ? `保証満期から1か月以内限定。既存保証の月額と修理上限金額を引き継いで申し込めます。`
                    : `保証満期から1か月以内のため、この車両でサブスクにお申し込みいただけます。`,
                )}
                onClick={() =>
                  f7.views.main.router.navigate(paths.subscriptionInheritance, {
                    props: {
                      inheritanceStatus: subscriptionInheritanceStatus,
                    },
                  })
                }
              />
            </div>
          )}
          {isWarrantyExpired && !hasInheritanceSubscription && (
            <div className="u-margin-top-sm">
              <IconCard
                path={paths.noticeWarrantyDetail}
                isTransition
                iconType="alert"
                headingFontSize="14px"
                iconFontSize="30px"
                color="danger"
                heading="保証が失効しております"
                supplement={perse(
                  `現在、保証契約が失効しておりますので、保証サービスの役務を停止しております​`,
                )}
              />
            </div>
          )}
          {isRegisterPaymentWarranty && (
            <div
              className={style['notice-box']}
              style={{ background: '#FFEFC6' }}
              onClick={handlePaymentRegistAlert}
            >
              <div className={style['notice-box-icon-title']}>
                <i
                  className="icon-confirm-notification"
                  style={{ color: 'orange' }}
                />
                <div className="notice-box__info-title">
                  保証の決済方法の登録をお願いします。
                </div>
              </div>
              <i className="icon-right" />
            </div>
          )}
          {isShowPrepaidInfo && (
            <div
              className={style['notice-box']}
              style={{ background: '#FFEFC6' }}
              onClick={handlePaymentRegistAlertPrepaid}
            >
              <div className={style['notice-box-icon-title']}>
                <i
                  className="icon-confirm-notification"
                  style={{ color: 'orange' }}
                />
                <div className="notice-box__info-title">
                  プリペイド積み立ての決済方法の登録をお願いします。
                </div>
              </div>
              <i className="icon-right" />
            </div>
          )}
          {hasSubscriptionNotice &&
            (subscriptionApplyStatus === 1 ||
              subscriptionApplyStatus === 2) && (
              <div
                className={style['notice-box']}
                style={{ background: '#E3F2FD' }}
                onClick={() =>
                  // apply_status 1（初回決済前）/ 2（継続登録前）はいずれも決済から再開
                  f7.view.main.router.navigate(paths.subscriptionPayment, {
                    props: {
                      subscriptionId,
                      tStockCarId: subscriptionTStockCarId,
                    },
                  })
                }
              >
                <div className={style['notice-box-icon-title']}>
                  <i
                    className="icon-confirm-notification"
                    style={{ color: '#1E55E6' }}
                  />
                  <div className="notice-box__info-title">
                    {subscriptionApplyStatus === 2
                      ? '来月以降のお支払い方法の登録が必要です'
                      : isExistingCarFlow
                      ? 'サービス開始にはお支払いが必要です'
                      : 'サービス開始にはお支払いと車検証の登録が必要です'}
                  </div>
                </div>
                <i className="icon-right" />
              </div>
            )}
          {hasSubscriptionNotice &&
            subscriptionApplyStatus === 0 &&
            subscriptionStatus === 2 && (
              <div
                className={style['notice-box']}
                style={{ background: '#FFF3E0' }}
                onClick={() =>
                  f7.view.main.router.navigate(paths.subscriptionComplete, {
                    props: {
                      subscriptionId,
                      tStockCarId: subscriptionTStockCarId,
                    },
                  })
                }
              >
                <div className={style['notice-box-icon-title']}>
                  <i
                    className="icon-confirm-notification"
                    style={{ color: '#E65100' }}
                  />
                  <div className="notice-box__info-title">
                    {isExistingCarFlow
                      ? '車両の紐付けが完了していません'
                      : 'サービス開始には車検証の登録が必要です'}
                  </div>
                </div>
                <i className="icon-right" />
              </div>
            )}
          {news && (
            <div
              className={style['notice-box']}
              onClick={() => handleClickNews(String(news?.id))}
            >
              <div className={style['notice-box-icon-title']}>
                <i
                  className="icon-confirm-notification"
                  style={{ color: 'orange' }}
                />
                <div className="notice-box__info-title">{news?.title}</div>
              </div>
              <i className="icon-right" />
            </div>
          )}
        </div>
        <RankBanner redirectPage={paths.home} />
        <div className={style['menu-wrapper']}>
          <div className="heading-with-button u-font-size-xl u-margin-bottom-sm">
            <span className="u-font-bold">メニュー</span>
          </div>
          <div className={style['menu-buttons-wrapper']}>
            {isShowSelfScan && (
              <div className={style['button-wrapper']}>
                <ButtonIconVertical
                  label={`車両状態\nセルフスキャン`}
                  iconType="state"
                  path={paths.selfScanStart}
                />
              </div>
            )}
            <div className={style['button-wrapper']}>
              <ButtonIconVertical
                label="メンテナンス"
                iconType="calendar-maintenance"
                path={paths.maintenance}
              />
            </div>
            {/* 確定前は false 初期値のせいで「保証管理」→「サブスク」と入れ替わるため、
                subscriptionLoaded が立つまではスケルトンを出してちらつきを防ぐ */}
            {!subscriptionLoaded && (
              <div className={style['button-wrapper']}>
                <ButtonIconVerticalSkeleton />
              </div>
            )}
            {subscriptionLoaded &&
              !(
                isShowSubscription ||
                hasSubscription ||
                hasSubscriptionInProgress ||
                hasInheritanceSubscription
              ) && (
                <div className={style['button-wrapper']}>
                  <ButtonIconVertical
                    label="保証管理"
                    iconType="guarantee"
                    path={paths.warranty('false')}
                  />
                </div>
              )}
            {subscriptionLoaded &&
              (isShowSubscription ||
                hasSubscription ||
                hasSubscriptionInProgress ||
                hasInheritanceSubscription) && (
                <div className={style['button-wrapper']}>
                  <ButtonIconVertical
                    label={
                      hasSubscription
                        ? `サブスク\n契約中`
                        : hasSubscriptionInProgress
                        ? `サブスク\n手続き中`
                        : hasInheritanceSubscription
                        ? `サブスク\n申込`
                        : `サブスク\nサービス`
                    }
                    iconType="guarantee"
                    onClick={
                      hasSubscriptionInProgress
                        ? handleClickSubscriptionInProgress
                        : undefined
                    }
                    path={
                      hasSubscription
                        ? paths.subscriptionMyPage
                        : hasSubscriptionInProgress
                        ? ''
                        : hasInheritanceSubscription
                        ? paths.subscriptionInheritance
                        : paths.subscriptionDetail
                    }
                  />
                </div>
              )}
            <div className={style['button-wrapper']}>
              <ButtonIconVertical
                label="車両情報"
                iconType="info"
                path={paths.car(String(selectedCar?.m_car_id) || '')}
              />
            </div>
            {isShowCarAssetValue && (
              <div className={style['button-wrapper']}>
                <ButtonIconVertical
                  label={`資産価値\n予測`}
                  iconType="car"
                  path={paths.carAssetValue}
                />
              </div>
            )}
            {isrRoyalAutoId && (
              <div
                className={style['button-wrapper']}
                onClick={handleRedirectToMaintenanceReservation}
              >
                <ButtonIconVertical
                  label="メンテナンス予約"
                  iconType="tool-maintenance"
                  path=""
                />
              </div>
            )}
            {(isAdContractor || isLinkDriveOwner) &&
              isShowDriveRecord &&
              selectedCar &&
              isDelivered(selectedCar?.delivery_complete_flg) && (
                <div className={style['button-wrapper']}>
                  <ButtonIconVertical
                    label="運転記録"
                    iconType="car"
                    path={paths.driveRecord}
                  />
                </div>
              )}
            {isDelivered(selectedCar?.delivery_complete_flg) && (
              <div className={style['button-wrapper']}>
                <ButtonIconVertical
                  label={`コネクター\n設定`}
                  iconType="notification-settings"
                  path={paths.connectorSetting}
                  // NOTE: 暫定対応【マイくる】修正箇所一覧(No.10) https://docs.google.com/spreadsheets/d/1ZhJCr4w7pkkwqMeowrMtLmR01FiTeEdX/edit?gid=105261587#gid=105261587
                  isDisabled={
                    store.state.customerInfo.m_affiliate_id ===
                    affiliateId.SANWA
                  }
                />
              </div>
            )}
            {isrRoyalAutoId && (
              <>
                {listExternalServiceURL.map(({ text, href, icon }) => (
                  <div
                    key={text}
                    className={style['button-wrapper']}
                    onClick={() => handleRedirect(href)}
                  >
                    <ButtonIconVertical label={text} iconType={icon} path="" />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        {isCategoriesLoading ? (
          <div className={style['maintenance-wrapper']}>
            <Skeleton height="150px" radius="12px" />
          </div>
        ) : (
          categories && (
            <div className={style['maintenance-wrapper']}>
              <ServicePromotionCard />
            </div>
          )
        )}
        {isBannersLoading ? (
          <div className={style['maintenance-wrapper']}>
            <div className="heading-with-button u-font-size-xl u-margin-bottom-sm u-margin-top-sm">
              <span className="u-font-bold">おすすめ商品</span>
            </div>
            <Skeleton height="120px" radius="8px" />
          </div>
        ) : (
          banners &&
          banners.length > 0 && (
            <div className={style['maintenance-wrapper']}>
              <div className="heading-with-button u-font-size-xl u-margin-bottom-sm u-margin-top-sm">
                <span className="u-font-bold">おすすめ商品</span>
              </div>
              <BannerList banners={banners} />
            </div>
          )
        )}
        {isShowCarWashService && (
          <div className={style['maintenance-wrapper']}>
            <CardRecommend />
          </div>
        )}
        <div className={style['maintenance-wrapper']}>
          <div className="heading-with-button u-font-size-xl u-margin-bottom-sm u-margin-top-sm">
            <span className="u-font-bold">整備</span>
          </div>
          <RequestMaintenanceButton
            handleClickButton={handleClickMaintenanceButton}
          />
        </div>
        {!isPc && isBrainTrainingDisplay && <BrainTraining />}
        {isMileageDisplay && <AdTelemileage />}
      </div>
    </PageTemplateV2>
  );
};
